import "twin.macro";

import { useState } from "react";
import { CSVReader } from "react-papaparse";
import { useDispatch, useSelector } from "react-redux";

import { Close, GetApp } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
} from "@mui/material";

import { uniqBy } from "lodash";

import { useTerritoriesListQuery } from "@features/territories";
import { clearError, setError } from "@redux/slices/errorSlice";
import asyncPool from "@utility/asyncPool";
import { downloadAsCsv } from "@utils/csv";

import { useCreateAddressImportMutation } from "../addressQueries";
import { bulkAddressColumns, handleAddressCSV } from "./validateAddressCsv";

type ModalStatus =
  | "pendingFile"
  | "validating"
  | "uploading"
  | "complete"
  | "error";

const errorTitles = {
  pendingFile: "Could not upload file",
  validating: "The following errors need to be fixed before uploading",
  uploading: "The addresses have been uploaded with the following error(s)",
  error: "An Error occurred",
};

type BulkUploadAddressesModalProps = {
  onClose: () => void;
};

const BulkUploadAddressesModal = ({
  onClose,
}: BulkUploadAddressesModalProps) => {
  const dispatch = useDispatch();

  const [status, setStatus] = useState<ModalStatus>("pendingFile");
  const [errors, setErrors] = useState<string[]>([]);
  const [percentUploaded, setPercentUploaded] = useState(0);

  const importAddress = useCreateAddressImportMutation();

  const {
    email,
    currentTerritoryId,
    organization: { addressBookType },
  } = useSelector((state: any) => state.currentUser);

  const { data: territories = [] } = useTerritoriesListQuery();

  const handleFileUpload = async (data) => {
    dispatch(clearError());
    setErrors([]);
    setPercentUploaded(0);

    try {
      setStatus("validating");

      const countries = uniqBy(
        territories.flatMap((t) => t.countries),
        "id"
      );

      const states = uniqBy(
        territories.flatMap((t) => t.states),
        "id"
      );

      const territoryName = territories.find(
        (t) => t.id === currentTerritoryId
      )?.name;

      const { validData, errors: csvValidationErrors } = handleAddressCSV(
        data,
        states,
        countries,
        addressBookType
      );

      if (csvValidationErrors.length > 0) {
        setErrors(csvValidationErrors);
        return;
      }

      setStatus("uploading");

      const res = await asyncPool(
        3,
        validData,
        async (address) =>
          importAddress.mutateAsync({
            emailList: [email],
            territoryList: [territoryName!],
            ...address,
          }),
        () => setPercentUploaded((prev) => prev + 1 / validData.length)
      );

      if (res.errors) {
        setErrors(res.errors.map((e) => e.message));
      }
      setStatus("complete");
    } catch (err: unknown) {
      console.error(err);
      setErrors([err instanceof Error ? err.message : String(err)]);
      setStatus("error");
      dispatch(setError({ error: err, source: "Bulk Address Upload" }));
    }
  };

  const handleFileUploadError = (err) => {
    dispatch(
      setError({ error: err.toString(), source: "Address File Upload" })
    );
    setErrors([err.toString()]);
    setStatus("error");
  };

  const resetStatus = () => {
    setStatus("pendingFile");
    setErrors([]);
    setPercentUploaded(0);
  };

  return (
    <Dialog open fullWidth maxWidth="md">
      <DialogTitle tw="flex justify-between items-start">
        Upload Bulk Addresses
        <IconButton onClick={onClose} edge="end">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent tw="space-y-6">
        {status === "pendingFile" && (
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={() =>
              downloadAsCsv(
                [bulkAddressColumns(addressBookType)],
                "bulk-address-form.csv"
              )
            }
          >
            DOWNLOAD CSV TEMPLATE
          </Button>
        )}
        {errors.length === 0 && (
          <div tw="bg-neutral-100 rounded-xl p-4">
            {status === "pendingFile" && (
              <CSVReader
                onFileLoad={handleFileUpload}
                onError={handleFileUploadError}
                config={{ header: true }}
              >
                Upload CSV file or drag and drop
              </CSVReader>
            )}
            {status === "validating" && "Validating..."}
            {status === "uploading" && "Uploading..."}
            {status === "complete" && "Upload complete!"}
            {status === "uploading" && (
              <LinearProgress
                tw="mt-4"
                variant="determinate"
                value={percentUploaded * 100}
              />
            )}
          </div>
        )}
        {errors.length > 0 && (
          <div tw="">
            <h3 tw="text-2xl text-neutral-600 mb-3">{errorTitles[status]}</h3>
            <ul tw="font-mono py-3 px-6 rounded bg-neutral-100 text-neutral-800 space-y-2">
              {errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}
        {status === "pendingFile" && (
          <p tw="text-base text-neutral-600">
            Upload Bulk Address allows you to upload a csv file of addresses.
            You must first download the form, and make sure to leave the titles
            of the column heads the same. When adding states or provinces, you
            should enter them as the state code (CA, MN, etc ..) and when
            entering the Country, you must enter the full name of the country
            (United States, United Kingdom, etc ...). State / Province is
            required if you are in the United States or Canada, otherwise Region
            is an optional field.
          </p>
        )}
      </DialogContent>
      <DialogActions>
        {(errors.length > 0 || status === "complete") && (
          <Button variant="outlined" onClick={resetStatus}>
            Upload new file
          </Button>
        )}
        {(status === "complete" ||
          (status === "uploading" && errors.length > 0)) && (
          <Button variant="contained" onClick={onClose}>
            Finish
          </Button>
        )}
        {status === "pendingFile" && (
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadAddressesModal;
